import { AccessTime, Add, ArrowForward, Delete, ExpandMore, FilterAlt, SaveAlt, SettingsBackupRestore, Sort, Upload, UploadFile } from '@mui/icons-material';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink} from "react-router-dom";
import { useSocket } from '../context/useSocket';
import { exportDataHelper } from '../helpers/exportDataHelper';
import { filterWells } from '../helpers/filterWells';
import { deleteList, makeListCurrent, selectLists } from '../reducers/listSlice';
import { selectOptions, setShowWellListFilter } from '../reducers/optionsSlice';
import { selectProjects } from '../reducers/projectSlice';
import { selectUser } from '../reducers/userSlice';
import { deleteWell, deleteWells, selectWells } from '../reducers/wellSlice';

const useStyles = makeStyles(theme => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    marginBottom: 10,
    height: 'auto',
    // overflow: 'auto'
  },
  details: {
    alignItems: 'center',
    padding: 10,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'auto',
    height: 'auto',
    // overflow: 'scroll',
  },
  table: {
    minHeight: '100px',
    width: '100%'
  },
  listButtons: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between'
  },
  status: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: 'calc(100% - 10px)',
    marginTop: 10,
    marginBottom: 5
  },
  chip: {
    marginRight: 5
  },
  empty: {
    height: 'auto',
    width: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 200
  }
}));

const WellList = ({projectId, listId, setNewWellVisible, setExportDataVisible}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];

  const lists = useSelector(selectLists);
  const list = lists?.byId?.[listId];
  const filters = list?.filters;
  const allWells = useSelector(selectWells);

  const [selectionModel, setSelectionModel] = useState([]);
  const [sortWellsDesc, setSortWellsDesc] = useState(true);

  const { socket } = useSocket();


  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      width: 125,
      editable: false,
      // sortable: false,
      // filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ textDecoration: 'none' }}
          startIcon={params.value === 'idle' ? <SettingsBackupRestore /> : <ArrowForward />}
          to="/forecast"
          component={RouterLink}
          onClick={() => sendProcessRequest([ params.id ], false)}
          // onClick={() => handleProcessClick([ params.id ])}
        >
          {params.value === "idle" ? "Process" : "Open"}
        </Button>
      ),
    },
    {
      field: 'wellUwi',
      headerName: 'Well UWI',
      width: 200,
      editable: false,
    },
    {
      field: 'wellLicensee',
      headerName: 'Licensee',
      width: 200,
      editable: false,
    },
    {
      field: 'onProductionDate',
      headerName: 'On Production',
      width: 150,
      editable: false
    },
    {
      field: 'wellFluids',
      headerName: 'Well Fluids',
      width: 200,
      editable: false,
      renderCell: (params) => (
        <>
          {params?.value?.includes('oil') && <Chip label="Oil" size="small" className={classes.chip} />}
          {params?.value?.includes('gas') && <Chip label="Gas" size="small" className={classes.chip} />}
          {params?.value?.includes('condensate') && <Chip label="Condensate" size="small" className={classes.chip} />}
          {params?.value?.includes('water') && <Chip label="Water" size="small" className={classes.chip} />}
        </>
      ),
    },
    { 
      field: 'eurOil',
      headerName: 'Oil EUR P50',
      width: 100,
      editable: false
    },
    { 
      field: 'eurGas',
      headerName: 'Gas EUR P50',
      width: 100,
      editable: false
    },
    { 
      field: 'eurCondensate',
      headerName: 'Condensate EUR P50',
      width: 100,
      editable: false
    },
    { 
      field: 'trajectory',
      headerName: 'Trajectory',
      width: 150,
      editable: false
    },
    {
      field: 'lastModified',
      headerName: 'Last Modified',
      width: 150,
      editable: false
    },
    {
      field: 'download',
      headerName: 'Download CSV',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          // style={{ marginLeft: 16 }}
          startIcon={<SaveAlt />}
          onClick={e => handleDownload(e, params.id, params?.row?.listId)}
          disabled={params?.row?.status !== 'processed'}
        >
          Download
        </Button>
      ),
    },
    {
      field: 'delete',
      headerName: 'Delete',
      width: 125,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          // style={{ marginLeft: 16 }}
          startIcon={<Delete />}
          onClick={(e) => {
            handleDeleteWell(e, params?.row?.listId, params?.id);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const demoUser = user?.value?.subscription === 'Demo';

  const wells = useMemo(() => {
    return filterWells(list?.wellUwis, allWells, filters);
  }, [allWells, filters, sortWellsDesc, lists]); //eslint-disable-line

  return (
    <Paper key={listId} className={classes.list}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            {lists?.byId[listId]?.name}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          
          <div className={classes.listButtons}>
            <div>
              <Tooltip title="Add wells">
                <span><Button 
                  color="primary" 
                  variant="contained" 
                  startIcon={<Add />} 
                  onClick={e => handleNewWellClick(e, listId)}
                  style={{marginRight: 10}}
                  disabled={demoUser}
                  size="small"
                >
                  Add Wells
                </Button></span>
              </Tooltip>
              <Tooltip title="Process selected wells">
                <span><Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SettingsBackupRestore />}
                  style={{marginRight: 10}}
                  onClick={() => sendProcessRequest(selectionModel, false)}
                  disabled={selectionModel?.length === 0}
                  size="small"
                >
                  Process Selected
                </Button></span>
              </Tooltip>
              <Tooltip title="Delete selected wells">
                <span><Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  startIcon={<Delete />}
                  onClick={() => dispatch(deleteWells({ uwis: selectionModel, listId: listId }))}
                  disabled={selectionModel?.length === 0}
                >
                  Delete Selected
                </Button></span>
              </Tooltip>
            </div>
            <div>
              <Button edge="end" size="small" color="primary" variant="text" startIcon={<Upload />} disabled>
                Save
              </Button>  
              <Button edge="end" size="small" color="primary" variant="text" startIcon={<UploadFile />} style={{marginRight: 10}} disabled>
                Import CSV
              </Button>
              <Tooltip title="Download CSV">
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  style={{ marginRight: 10 }}
                  startIcon={<SaveAlt />}
                  onClick={e => handleExportData(e)}
                >
                  Download CSV
                </Button>
              </Tooltip>
              <Tooltip title="Filter wells">
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  style={{ marginRight: 10 }}
                  startIcon={<FilterAlt />}
                  onClick={e => handleFilterClick(e, listId)}
                >
                  Filter
                </Button>
              </Tooltip>
              <Tooltip title="Filter wells">
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  style={{ marginRight: 10 }}
                  startIcon={<Sort />}
                  onClick={e => setSortWellsDesc(prev => !prev)}
                >
                  Sort
                </Button>
              </Tooltip>
            </div>
          </div>

          <div className={classes.status}>
            <Typography fontSize={12}>{`Total wells: ${wells.allUwis?.length || 0} | Sorted by: ${sortWellsDesc ? 'newest' : 'oldest'} | Filters: ${filters ? Object.keys(filters)?.length : 0}`}</Typography>
            <div style={{display: 'flex', alignItems: 'center'}}>
              <AccessTime fontSize="12px" className={classes.icon} style={{marginRight: 5}} />
              <Typography fontSize="12px" color="textSecondary">
                {`Last modified: ${new Date(lists.byId[listId]?.lastModified)?.toLocaleString()}`}
              </Typography>
            </div>
          </div>
          <DataGrid
            className={classes.table}
            checkboxSelection
            onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
            selectionModel={selectionModel}
            rows={getRows(listId)}
            columns={columns}
            rowsPerPageOptions={[10,25,50,100]}
            autoHeight={true}
            disableSelectionOnClick
            sx={{fontSize: 12}}
            rowHeight={40}
          />
        </AccordionDetails>
        <AccordionActions>
          <Button type="text" color="secondary" onClick={e => deleteListClick(e, projectId, listId)}>
            Delete
          </Button>
        </AccordionActions>
      </Accordion>
    </Paper> 
  );

  // FUNCTIONS
  function sendProcessRequest(uwis, reFit) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });
      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params: {},
          localParams,
          reFit,
          assistedForecast: false, 
          tcParams: false
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };

  // function handleProcessClick(uwis) {
  //   dispatch(setProcessJobs({ uwis: uwis }));
  // };
  
  function handleNewWellClick(e, listId) {
    e.stopPropagation();
    dispatch(makeListCurrent(listId));
    setNewWellVisible(true);
  };

  function handleFilterClick(e, listId) {
    e.stopPropagation();
    !options?.showWellListFilter ? dispatch(setShowWellListFilter(listId)) : dispatch(setShowWellListFilter(false));
  };

  function getRows(listId) {
    try {
      let rows = [];
  
      if (!lists?.byId?.[listId]?.wellUwis?.length > 0) return [];

      // let wellUwis = [...lists?.byId?.[listId]?.wellUwis];
      let wellUwis = [...wells.allUwis];

      if (!wellUwis?.length > 0) return [];

      if (sortWellsDesc) {
        wellUwis = wellUwis.reverse();
      };
      wellUwis?.forEach((wellUwi) => {
        rows.push({
          id: wellUwi,
          listId: listId,
          status: wells.byUwi[wellUwi]?.status,
          wellUwi: wells.byUwi[wellUwi]?.formattedUwi,
          lastModified: wells.byUwi[wellUwi]?.lastModified ? (new Date(wells.byUwi[wellUwi]?.lastModified)?.toISOString()?.split('T')?.[0]) : "",
          onProductionDate: wells.byUwi[wellUwi]?.details?.on_prod_date ? (new Date(wells.byUwi[wellUwi]?.details?.on_prod_date)?.toISOString()?.split('T')?.[0]) : "",
          trajectory: wells.byUwi[wellUwi]?.details?.trajectory || "",
          eurOil: wells.byUwi[wellUwi]?.status === 'processed' ? Number(wells.byUwi[wellUwi]?.eur?.byFluid?.oil?.p50?.cumVolume?.toFixed(1) || 0) : 0,
          eurGas: wells.byUwi[wellUwi]?.status === 'processed' ? Number(wells.byUwi[wellUwi]?.eur?.byFluid?.gas?.p50?.cumVolume?.toFixed(1) || 0) : 0,
          eurCondensate: wells.byUwi[wellUwi]?.status === 'processed' ? Number(wells.byUwi[wellUwi]?.eur?.byFluid?.condensate?.p50?.cumVolume?.toFixed(1) || 0) : 0,
          wellList: lists.byId[listId]?.name || "",
          wellFluids: wells.byUwi[wellUwi]?.production?.fluids || [],
          wellLicensee: wells.byUwi[wellUwi]?.details?.licensee || "",
        });
      });
      return rows;
    } catch (error) {
      console.log(error);
      return [];
    };
  };

  function handleDownload(e, wellUwi) {
    e.preventDefault();
    e.stopPropagation();
    exportDataHelper(wellUwi, "", "metric", [wellUwi], [], [], [], lists, wells, {}, {});
  };

  function handleExportData(e) {
    e.stopPropagation();
    setExportDataVisible(true);
  };

  function handleDeleteWell(e, listId, wellUwi) {
    e.preventDefault();
    e.stopPropagation();

    dispatch(deleteWell({ listId: listId, wellUwi: wellUwi }));
  };

  function deleteListClick(e, projectId, listId) {
    e.preventDefault();
    dispatch(deleteList({projectId, listId}));
  };
};

export default WellList;
